import { createFileRoute, notFound, redirect } from '@tanstack/react-router';
import { resolveLegacyPath } from '@/lib/legacyRedirects';

/**
 * The catch-all.
 *
 * Anything that matches no real route lands here — most of the retired URLs
 * (`/receiving`, `/inventory/lots`, `/cash-management`) have no route of their
 * own, so this is where they get rewritten. It sits at the root, *outside*
 * `_app`, on purpose: the rewrite has to happen before the auth guard, so a
 * signed-out operator following an old bookmark is sent to the canonical URL
 * first and only then bounced to login. Otherwise they would sign in and land
 * on `/login?redirect=<dead url>` → 404.
 *
 * The handful of legacy paths that *do* collide with a live route
 * (`/products/departments` matches `/products/$id`) never reach this route;
 * `__root.tsx` catches those with the same table before the match is resolved.
 *
 * Whatever is left is genuinely unknown and gets the root's not-found screen.
 */
export const Route = createFileRoute('/$')({
  beforeLoad: ({ location }) => {
    const destination = resolveLegacyPath(location.pathname, location.searchStr);
    if (destination) {
      // `replace` so the dead URL does not sit in history behind the live one —
      // Back from the new screen should leave the console, not bounce.
      throw redirect({ href: destination, replace: true });
    }
    throw notFound();
  },
  // Never rendered — beforeLoad always throws. Present so a future edit that
  // stops throwing degrades to the friendly screen rather than a blank one.
  component: () => null,
});
